import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AUTH_ISSUER_ENDPOINT_PATTERN } from '../config/auth-recovery.config';
import {
	getTokenTypeForEndpoint,
	TokenType,
} from '../config/endpoint-token-catalog';
import {
	TokenExpiredError,
	TurnstileVerificationError,
} from '../models/auth/auth-token.models';
import {
	AuthRecoveryService,
	RefreshableTokenType,
	TokenRefreshOutcome,
} from '../services/auth/auth-recovery.service';
import { AuthTokenStoreService } from '../services/auth/auth-token-store.service';

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
	constructor(
		private authTokenStore: AuthTokenStoreService,
		private injector: Injector
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		if (AUTH_ISSUER_ENDPOINT_PATTERN.test(request.url)) {
			return this.handleIssuerRequest(request, next);
		}

		const tokenType = getTokenTypeForEndpoint(request.url, request.method);

		if (tokenType === TokenType.NONE) {
			return next.handle(request);
		}

		return this.handleAuthorizedRequest(
			request,
			next,
			this.resolveRefreshableType(tokenType),
			false
		);
	}

	/**
	 * Los endpoints que emiten los tokens no se reintentan: si son ellos los que
	 * rechazan, no hay credencial que renovar.
	 */
	private handleIssuerRequest(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === FORBIDDEN || error.status === UNAUTHORIZED) {
					this.authRecovery.goToRejectedByPermissions(
						`${request.url} respondió ${error.status}`
					);
				}
				return throwError(() => error);
			})
		);
	}

	/**
	 * Envía la petición con el token que le corresponde. Ante un 403 reemite el
	 * token y reintenta una sola vez; si el segundo intento vuelve a dar 403, el
	 * proceso termina en la pantalla de rechazo.
	 */
	private handleAuthorizedRequest(
		request: HttpRequest<unknown>,
		next: HttpHandler,
		tokenType: RefreshableTokenType,
		isRetry: boolean
	): Observable<HttpEvent<unknown>> {
		const token = this.getValidToken(tokenType);

		if (!token) {
			const missingTokenError = new TokenExpiredError(tokenType);
			return isRetry
				? this.rejectAndThrow(
						missingTokenError,
						`el token ${tokenType} sigue sin estar disponible tras reemitirlo`
				  )
				: this.refreshAndRetry(
						request,
						next,
						tokenType,
						missingTokenError,
						`sin token ${tokenType} para ${request.url}`
				  );
		}

		return next
			.handle(
				request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
			)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status !== FORBIDDEN) {
						return throwError(() => error);
					}
					if (isRetry) {
						return this.rejectAndThrow(
							error,
							`403 en ${request.url} con un token ${tokenType} recién emitido`
						);
					}
					return this.refreshAndRetry(
						request,
						next,
						tokenType,
						error,
						`403 en ${request.url}`
					);
				})
			);
	}

	private refreshAndRetry(
		request: HttpRequest<unknown>,
		next: HttpHandler,
		tokenType: RefreshableTokenType,
		originalError: Error | HttpErrorResponse,
		reason: string
	): Observable<HttpEvent<unknown>> {
		return this.authRecovery.refreshToken(tokenType).pipe(
			switchMap(outcome => {
				if (outcome === TokenRefreshOutcome.REFRESHED) {
					return this.handleAuthorizedRequest(request, next, tokenType, true);
				}
				if (outcome === TokenRefreshOutcome.REAUTH_REQUIRED) {
					return throwError(() => originalError);
				}
				// Cloudflare no pudo verificar el navegador. El backend no negó nada
				// —no hubo ni petición—, así que el proceso no termina en rechazo: la
				// petición falla y el socio conserva su avance.
				if (outcome === TokenRefreshOutcome.VERIFICATION_FAILED) {
					return throwError(() => new TurnstileVerificationError(reason));
				}
				return this.rejectAndThrow(
					originalError,
					`${reason}: no se pudo reemitir el token ${tokenType}`
				);
			})
		);
	}

	private rejectAndThrow(
		error: Error | HttpErrorResponse,
		reason: string
	): Observable<never> {
		this.authRecovery.goToRejectedByPermissions(reason);
		return throwError(() => error);
	}

	/**
	 * Devuelve el token solo si sigue vigente. Un token vencido se trata igual
	 * que uno ausente: se reemite antes de salir a la red, en lugar de gastar
	 * una petición que el gateway va a responder con 403.
	 */
	private getValidToken(tokenType: RefreshableTokenType): string | null {
		if (tokenType === TokenType.EVALUATION) {
			return this.authTokenStore.isEvaluationTokenValid()
				? this.authTokenStore.getEvaluationToken()
				: null;
		}
		return this.authTokenStore.isInitialTokenValid()
			? this.authTokenStore.getInitialToken()
			: null;
	}

	/**
	 * EVALUATION_OR_INITIAL reemite el token que realmente se está usando en la
	 * petición, no siempre el inicial. Se queda con el de evaluación mientras
	 * siga vigente; si ya venció cae al inicial, que es el que sí se puede
	 * reemitir sin volver a pedirle el OTP al socio.
	 */
	private resolveRefreshableType(tokenType: TokenType): RefreshableTokenType {
		if (
			tokenType === TokenType.INITIAL ||
			tokenType === TokenType.EVALUATION
		) {
			return tokenType;
		}
		return this.authTokenStore.isEvaluationTokenValid()
			? TokenType.EVALUATION
			: TokenType.INITIAL;
	}

	/**
	 * Se resuelve dentro de la petición y no en el constructor: AuthRecoveryService
	 * depende de HttpClient y resolverlo al construir el interceptor crearía un
	 * ciclo de DI.
	 */
	private get authRecovery(): AuthRecoveryService {
		return this.injector.get(AuthRecoveryService);
	}
}
